import { content } from "./content.js";

// Shell-owned adapter for the hosted Panel 2 lead form.
//
// Ownership boundary:
// - This widget owns the panel slot and the existing callback flow.
// - The hosted Panel 2 module owns its HTML, CSS, validation, reCAPTCHA,
//   backend calls, and success/back events.
// - This adapter only mounts the hosted module and translates those events
//   back into the callbacks that panel.js already understands.
const LEAD_FORM_JS_MODULE_PATH = "/js/prompt-to-trial/protege-trial.js";
const TRIAL_FORM_UNAVAILABLE_MESSAGE =
  `${content.submitErrorMessage} If the problem continues, contact support.`;

// savedBody preserves the Panel 1 DOM while Panel 2 temporarily occupies the
// same .tp-panel__body slot. activeSession prevents stale async imports/events
// from mutating the UI after the user backs out, closes, or reopens the panel.
let savedBody = null;
let activeSession = null;

export function show(panelEl, { question, onSubmit, onBack }) {
  cleanupActiveSession();

  const body = panelEl.querySelector(".tp-panel__body");
  const root = prepareRoot(body);
  const session = createSession({ panelEl, root, question, onSubmit, onBack });
  activeSession = session;

  renderLoading(root);
  startRemoteForm(session);
}

export function reset(panelEl) {
  cleanupActiveSession();
  restoreSavedBody(panelEl);
}

function createSession({ panelEl, root, question, onSubmit, onBack }) {
  // The hosted form communicates upward with DOM events. Keeping the listeners
  // here lets Panel 2 stay decoupled from Panel 3 while preserving this
  // codebase's existing callback contract.
  const session = {
    panelEl,
    root,
    question,
    remoteForm: null,
    onSubmit,
    onBack,
    handleBack: null,
    handleSubmit: null,
  };

  session.handleBack = () => {
    if (!isActiveSession(session)) return;
    back(panelEl, onBack);
  };

  session.handleSubmit = (event) => {
    if (!isActiveSession(session)) return;

    const detail = event.detail ?? {};
    const unlockToken = normalizeToken(detail.unlockToken);

    if (!unlockToken) {
      console.error("[TryProtege] Panel 2 submitted without unlockToken", {
        detailKeys: Object.keys(detail),
      });
      renderError(root, {
        message: TRIAL_FORM_UNAVAILABLE_MESSAGE,
        onRetry: () => show(panelEl, { question, onSubmit, onBack }),
        onBack: () => back(panelEl, onBack),
      });
      return;
    }

    cleanupActiveSession();
    restoreSavedBody(panelEl);

    // Panel 3 receives the token through panel.js. Do not store it in local
    // storage or put it in the URL.
    if (typeof onSubmit === "function") {
      onSubmit({
        question,
        formData: {
          unlockToken,
          transactionId: normalizeOptionalString(detail.transactionId),
          expiresIn: normalizeOptionalNumber(detail.expiresIn),
        },
      });
    }
  };

  root.addEventListener("protege:trial-form-back", session.handleBack);
  root.addEventListener("protege:trial-form-submitted", session.handleSubmit);

  return session;
}

async function startRemoteForm(session) {
  try {
    // Load the Panel 2 implementation only when the user reaches Panel 2.
    // Resolve the path against the page origin so a CDN-hosted tryprotege.js
    // still loads the form module from the host page, not from the CDN origin.
    // webpackIgnore is intentional: this module is hosted separately by the
    // Panel 2 team and must not be bundled into tryprotege.js.
    const trialFormModule = await import(
      /* webpackIgnore: true */ getLeadFormModuleUrl()
    );

    if (!isActiveSession(session)) return;

    const { initProtegeTrialForm } = trialFormModule;

    if (typeof initProtegeTrialForm !== "function") {
      throw new Error("Panel 2 module must export initProtegeTrialForm.");
    }

    session.root.replaceChildren();
    // Normal integration contract: the shell provides only the mount element
    // and the Panel 1 question. Form internals and endpoint defaults stay in
    // the hosted Panel 2 module.
    const remoteForm = await initProtegeTrialForm({
      root: session.root,
      question: session.question,
    });

    if (!isActiveSession(session)) {
      destroyRemoteForm(remoteForm);
      return;
    }

    session.remoteForm = remoteForm;
  } catch (error) {
    if (!isActiveSession(session)) return;

    // This fallback is only for adapter/module-loading failures. Field
    // validation and submit errors are owned by the hosted Panel 2 module.
    console.error("[TryProtege] could not load Panel 2 trial form", error);
    renderError(session.root, {
      message: TRIAL_FORM_UNAVAILABLE_MESSAGE,
      onRetry: () =>
        show(session.panelEl, {
          question: session.question,
          onSubmit: session.onSubmit,
          onBack: session.onBack,
        }),
      onBack: () => back(session.panelEl, session.onBack),
    });
  }
}

function prepareRoot(body) {
  // Panel 2 uses the same panel body slot as the other screens. Snapshot Panel
  // 1 before mounting so Back, Retry, and X-close can restore the original UI.
  if (!savedBody) {
    savedBody = document.createDocumentFragment();
    while (body.firstChild) savedBody.appendChild(body.firstChild);
  }

  body.replaceChildren();

  const root = document.createElement("div");
  root.className = "tp-lead-form";
  body.appendChild(root);
  return root;
}

function back(panelEl, onBack) {
  cleanupActiveSession();
  restoreSavedBody(panelEl);

  if (typeof onBack === "function") {
    onBack();
  }
}

function restoreSavedBody(panelEl) {
  if (!savedBody) return;

  const body = panelEl.querySelector(".tp-panel__body");
  body.replaceChildren();
  body.appendChild(savedBody);
  savedBody = null;
}

function cleanupActiveSession() {
  if (!activeSession) return;

  // Remove adapter listeners and let the hosted form clean up any in-flight
  // work it owns, such as pending fetches or field listeners.
  activeSession.root.removeEventListener(
    "protege:trial-form-back",
    activeSession.handleBack,
  );
  activeSession.root.removeEventListener(
    "protege:trial-form-submitted",
    activeSession.handleSubmit,
  );
  destroyRemoteForm(activeSession.remoteForm);
  activeSession = null;
}

function destroyRemoteForm(remoteForm) {
  // The hosted module may return { destroy } from initProtegeTrialForm.
  // Calling it keeps lifecycle ownership explicit across the team boundary.
  if (remoteForm && typeof remoteForm.destroy === "function") {
    remoteForm.destroy();
  }
}

function isActiveSession(session) {
  return activeSession === session;
}

function getLeadFormModuleUrl() {
  return new URL(LEAD_FORM_JS_MODULE_PATH, window.location.origin).href;
}

function renderLoading(root) {
  root.innerHTML = `
    <div class="tp-lead-form__status" role="status">
      Loading the trial form...
    </div>
  `;
}

function renderError(root, { message, onRetry, onBack }) {
  // Error UI lives in the adapter because a failed import/export means the
  // hosted form cannot render its own recovery state.
  root.innerHTML = `
    <div class="tp-lead-form__error" role="alert">
      <h2 class="tp-lead-form__error-heading">Could not load the trial form</h2>
      <p class="tp-lead-form__error-copy"></p>
      <div class="tp-lead-form__error-actions">
        <button class="tp-lead-form__button tp-lead-form__button--primary" type="button" data-retry-panel-2>
          Try again
        </button>
        <button class="tp-lead-form__button" type="button" data-back-panel-1>
          Back
        </button>
      </div>
    </div>
  `;

  root.querySelector(".tp-lead-form__error-copy").textContent = message;
  root.querySelector("[data-retry-panel-2]").addEventListener("click", onRetry);
  root.querySelector("[data-back-panel-1]").addEventListener("click", onBack);
}

function normalizeToken(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeOptionalString(value) {
  const text = typeof value === "string" ? value.trim() : "";
  return text || null;
}

function normalizeOptionalNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}
